import { storyblokEditable } from "@storyblok/react";
import config from "config/config";
import type { ISbComponentType } from "storyblok-js-client";
import {
  AboutUsTrustpilotSection,
  AboveTheFoldAutocompleteSection,
  AboveTheFoldCTASection,
  Accordion,
  AhPlusPricingPlans,
  AhTimeline,
  Anchor,
  BarcodeScannerButton,
  BlogLegacyContent,
  Button,
  Cards,
  CheckList,
  Collapsible,
  Column,
  ComparisonCards,
  CompensationFeeCards,
  Content,
  ContentWithImageSection,
  CTA,
  ErrorComponent,
  FeeModelCards,
  FeeOnHomepage,
  Flags,
  Heading,
  HelpQuestion,
  HeroForm,
  HeroImageSection,
  HorizontalDivider,
  Icon,
  Image,
  ImageCTA,
  InfoBox,
  LegalContent,
  Link,
  LinkBox,
  ListCard,
  Logo,
  OurFeesCompensationTable,
  OurTeamSection,
  PartnerCards,
  PartnersHero,
  PieChart,
  Popover,
  PressCoverageItem,
  Row,
  Schema,
  SideBarHeading,
  Spacer,
  Subscribe,
  Table,
  TestimonialsSection,
  TextSwitcher,
  Trustpilot,
  TrustpilotExtended,
  TrustpilotReviewCard,
  TrustpilotSlider,
  TrustpilotTestimonial,
  UnstyledListWithIcon,
  Unsubscribe,
  Modal,
  AhPlusModal,
  AirHelpPlusBenefitsSection,
  Aligner,
  WhyChooseAirHelpSection,
  WhyChooseAirHelp,
} from "../adapters";

import type { DynamicComponentProps } from "./DynamicComponent.d";

const developmentEnvironment = config.buildEnv === "development";

// resolve Storyblok components to Next.js components
const Components = {
  sideBarHeading: SideBarHeading,
  heading: Heading,
  content: Content,
  ctaSection: CTA,
  ctaImageSection: ImageCTA,
  legalContent: LegalContent,
  blogLegacyContent: BlogLegacyContent,
  image: Image,
  row: Row,
  column: Column,
  link: Link,
  linkBox: LinkBox,
  infoBox: InfoBox,
  checkList: CheckList,
  table: Table,
  subscribe: Subscribe,
  cards: Cards,
  button: Button,
  trustpilotReview: TrustpilotReviewCard,
  trustpilot: Trustpilot,
  anchor: Anchor,
  logo: Logo,
  flags: Flags,
  heroForm: HeroForm,
  trustpilotSlider: TrustpilotSlider,
  accordion: Accordion,
  helpQuestion: HelpQuestion,
  whyChooseAirHelp: WhyChooseAirHelp,
  spacer: Spacer,
  schema: Schema,
  comparisonCards: ComparisonCards,
  pieChart: PieChart,
  icon: Icon,
  textSwitcher: TextSwitcher,
  ahPlusPricingPlans: AhPlusPricingPlans,
  compensationFeeCards: CompensationFeeCards,
  feeModelCards: FeeModelCards,
  barcodeScannerButton: BarcodeScannerButton,
  aboveTheFoldCTASection: AboveTheFoldCTASection,
  trustpilotExtended: TrustpilotExtended,
  aboveTheFoldAutocompleteSection: AboveTheFoldAutocompleteSection,
  testimonialsSection: TestimonialsSection,
  pressCoverageItem: PressCoverageItem,
  contentWithImageSection: ContentWithImageSection,
  listCard: ListCard,
  heroImageSection: HeroImageSection,
  partnerCards: PartnerCards,
  partnersHero: PartnersHero,
  ourFeesCompensationTable: OurFeesCompensationTable,
  trustpilotTestimonial: TrustpilotTestimonial,
  ourTeamSection: OurTeamSection,
  aboutUsTrustpilotSection: AboutUsTrustpilotSection,
  collapsible: Collapsible,
  ahTimeline: AhTimeline,
  horizontalDivider: HorizontalDivider,
  feeOnHomepageContent: FeeOnHomepage,
  popover: Popover,
  unstyledListWithIcon: UnstyledListWithIcon,
  unsubscribe: Unsubscribe,
  modal: Modal,
  ahPlusModal: AhPlusModal,
  airHelpPlusBenefitsSection: AirHelpPlusBenefitsSection,
  aligner: Aligner,
  whyChooseAirHelpSection: WhyChooseAirHelpSection,
};

const DynamicComponent = ({ blok }: DynamicComponentProps) => {
  const Component = Components[blok.component] || (developmentEnvironment && ErrorComponent);
  if (!Component) return null;

  const sbEditable = storyblokEditable(blok);

  return <Component blok={blok} sbEditable={sbEditable} />;
};

export function renderDynamicComponent(body: ISbComponentType<string>[]) {
  return body ? body.map((blok) => <DynamicComponent blok={blok} key={blok._uid} />) : null;
}

export default DynamicComponent;
